import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { FormattedMessage, injectIntl } from 'react-intl';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { ErrorIndication } from '@atlassian/help-center-common-component/error-indication';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import { PasswordField } from '@atlassian/help-center-common-component/password-field';
import { ThemedButton } from '@atlassian/help-center-common-component/themed-button';
import { MAX_FULL_NAME_LENGTH_IN_CHARS } from '@atlassian/help-center-common-util/sign-up-form-validator';
import messages from './messages';

interface InvitedSignUpFormStatelessProps {
    email: string;
    fullName: string;
    onFullNameChange: (updatedFullName: string) => void;
    fullNameError?: FormattedMessage.MessageDescriptor;
    password: string;
    onPasswordChange: (updatedFullName: string) => void;
    passwordError?: FormattedMessage.MessageDescriptor;
    onSubmit: () => void;
    isSubmitting: boolean;
}

// Field lengths are greater than max allowed, to avoid silently cutting off pasted content
const FULL_NAME_FIELD_LENGTH_IN_CHARS = MAX_FULL_NAME_LENGTH_IN_CHARS + 10;
const PRIVACY_POLICY_LINK = 'https://www.atlassian.com/legal/privacy-policy';
const NOTICE_AND_DISCLAIMER_LINK = 'https://www.atlassian.com/legal/jira-service-desk-notice-and-disclaimer';

export class InvitedSignUpFormStateless extends React.Component<InvitedSignUpFormStatelessProps & InjectedIntlProps> {
    onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }
        this.props.onSubmit();
    };

    onFullNameChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        this.props.onFullNameChange(e.target.value);
    };

    render() {
        const { isSubmitting, intl, email, fullName, fullNameError, password, passwordError, onPasswordChange } =
            this.props;
        const fullNameErrorIndication = fullNameError && <ErrorIndication errorMessage={fullNameError} />;

        const passwordErrorMessage = passwordError && intl.formatMessage(passwordError);

        return (
            <Form id="invited-sign-up-form" onSubmit={this.onSubmit}>
                <FieldBase label={intl.formatMessage(messages.emailAddress)} htmlFor="user-email">
                    <Textfield
                        id="user-email"
                        data-testid="user-email"
                        placeholder={intl.formatMessage(messages.emailAddress)}
                        label={intl.formatMessage(messages.emailAddress)}
                        isDisabled
                        defaultValue={email}
                        name={'user-email'}
                    />
                </FieldBase>
                <FieldBase
                    label={intl.formatMessage(messages.fullNameLabel)}
                    htmlFor="full-name"
                    errorMessage={fullNameErrorIndication}
                >
                    <Textfield
                        id="full-name"
                        data-testid="full-name"
                        maxLength={FULL_NAME_FIELD_LENGTH_IN_CHARS}
                        onChange={this.onFullNameChange}
                        isInvalid={fullNameError !== undefined}
                        isDisabled={isSubmitting}
                        value={fullName}
                        // Disabling existing violations, should be fixed when revisited.
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                    />
                </FieldBase>
                <FieldBase label={intl.formatMessage(messages.passwordLabel)} htmlFor="password">
                    <PasswordField
                        id="password"
                        data-testid="password"
                        value={password}
                        onChange={onPasswordChange}
                        isDisabled={isSubmitting}
                        isInvalid={passwordError !== undefined}
                        errorMessage={passwordErrorMessage}
                        weakPasswords={[(email || '').trim()]}
                    />
                </FieldBase>
                <ConditionsContainer>
                    <FormattedMessage
                        {...messages.signUpConditions}
                        values={{
                            signUpButtonLabel: <i>{intl.formatMessage(messages.submitButton)}</i>,
                            privacyPolicyLink: (
                                <a href={PRIVACY_POLICY_LINK} target="_blank" rel="noopener noreferrer">
                                    {intl.formatMessage(messages.privacyPolicyLinkText)}
                                </a>
                            ),
                            noticeAndDisclaimerLink: (
                                <a href={NOTICE_AND_DISCLAIMER_LINK} target="_blank" rel="noopener noreferrer">
                                    {intl.formatMessage(messages.noticeAndDisclaimerLinkText)}
                                </a>
                            ),
                        }}
                    />
                </ConditionsContainer>
                <ButtonContainer>
                    <ThemedButton
                        id="sign-up-button"
                        type="submit"
                        appearance="primary"
                        shouldFitContainer
                        isLoading={isSubmitting}
                        actionSubjectId="submitInvitedSignUp"
                    >
                        {intl.formatMessage(messages.submitButton)}
                    </ThemedButton>
                </ButtonContainer>
            </Form>
        );
    }
}

export default injectIntl(InvitedSignUpFormStateless);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Form = styled.form`
    text-align: left;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ButtonContainer = styled.div`
    margin-top: ${token('space.200', '16px')};
    margin-bottom: ${token('space.400', '32px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ConditionsContainer = styled.div`
    margin-top: ${token('space.200', '16px')};
`;
